var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { PrimaryGrid } from "../../components/grid/PrimaryGrid";
import React, { useEffect, useMemo, useRef } from "react";
import { PageHeader } from "../../components/header/PageHeader";
import Button from "@mui/material/Button";
import { Box, Tooltip, useMediaQuery, Chip, useTheme, Select, MenuItem, FormControl, InputLabel, Skeleton, Typography, Tabs, Tab, TablePagination, CircularProgress, } from "@mui/material";
import { getGridNumericOperators, getGridStringOperators, } from "@mui/x-data-grid";
import { SearchNormal, Moneys, StatusUp, MoneyRecive, } from "iconsax-react";
import { FilteredInput } from "@ui/components/inputs";
import { useNavigate } from "react-router-dom";
import { ProjectCard } from "../../components/card/ProjectCard";
import { MetricCard } from "../../components/card";
import { highlightText } from "../../utils/highlightText";
import { smartTruncateString, } from "../../utils/smartTruncation";
import { CircularProgressbar, buildStyles } from "react-circular-progressbar";
import { formatDutchNumberWithSuffix, formatDutchNumberWithKSuffixOnly, } from "../../utils/numberFormat";
import { useGetProjectSnapshotsMutation, } from "../../../features/projects/projectsApi";
// Helper function to format total values with negative formatting (red color and brackets)
const formatTotalValue = (value, unitType) => {
    const absValue = Math.abs(value);
    let formattedValue;
    if (unitType === "full") {
        formattedValue = absValue.toLocaleString("nl-NL", {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        });
    }
    else if (unitType === "M") {
        formattedValue = formatDutchNumberWithSuffix(absValue);
    }
    else {
        formattedValue = formatDutchNumberWithKSuffixOnly(absValue);
    }
    if (value < 0) {
        return React.createElement("span", { style: { color: "red" } },
            "(",
            formattedValue,
            ")");
    }
    return formattedValue;
};
import { useSnackbar } from "notistack";
import { CloseCircle } from "iconsax-react";
import { useCompany } from "../../../context/CompanyContext";
import { usePermissions } from "../../../context/PermissionContext";
// Utility function to format project name with search highlighting and smart truncation
const formatProjectName = (projectName, searchTerm, isSearchResult = false, tooltipPath) => {
    if (!projectName)
        return "N/A";
    const shouldHighlight = isSearchResult && searchTerm && searchTerm.trim() !== "";
    // Use tooltipPath if available, otherwise use the original logic
    const tooltipContent = tooltipPath || projectName;
    // Check if the name contains "..." pattern (parent path)
    const pathMatch = projectName.match(/^(.+\.\.\.)\/(.+)$/);
    if (pathMatch) {
        const [, parentPath, originalName] = pathMatch;
        const fullPath = `${parentPath}/${originalName}`;
        // Apply smart truncation to the full path (string version for highlighting)
        const truncatedPathString = smartTruncateString(fullPath, 100);
        // Apply highlighting to the truncated result
        const highlightedPath = shouldHighlight
            ? highlightText(truncatedPathString, searchTerm)
            : truncatedPathString;
        // Wrap with tooltip showing tooltipPath or full path
        return (React.createElement(Tooltip, { title: tooltipContent, arrow: true },
            React.createElement("span", null, highlightedPath)));
    }
    // If no path pattern, apply smart truncation and highlighting
    const truncatedNameString = smartTruncateString(projectName, 100);
    const highlightedName = shouldHighlight
        ? highlightText(truncatedNameString, searchTerm)
        : truncatedNameString;
    // Wrap with tooltip showing tooltipPath or project name
    return (React.createElement(Tooltip, { title: tooltipContent, arrow: true },
        React.createElement("span", null, highlightedName)));
};
// Filter operators - only use 'contains' operator for strings and '>=' for numbers
const stringFilterOperators = getGridStringOperators().filter((operator) => operator.value === "contains");
const numericFilterOperators = getGridNumericOperators().filter((operator) => operator.value === ">=");
// Custom filter operator for status dropdown
const statusFilterOperators = [
    {
        label: "is",
        value: "is",
        getApplyFilterFn: (filterItem) => {
            if (!filterItem.value) {
                return null;
            }
            return (params) => {
                var _a;
                const cellValue = ((_a = params.value) !== null && _a !== void 0 ? _a : "").toString().toLowerCase().trim();
                const filterValue = filterItem.value.toLowerCase().trim();
                return cellValue === filterValue;
            };
        },
        InputComponent: (props) => {
            const { item, applyValue } = props;
            const handleFilterChange = (event) => {
                applyValue(Object.assign(Object.assign({}, item), { value: event.target.value }));
            };
            return (React.createElement(Select, { value: item.value || "", onChange: handleFilterChange, displayEmpty: true, size: "small", sx: { minWidth: 150 } },
                React.createElement(MenuItem, { value: "" },
                    React.createElement("em", null, "Alles")),
                React.createElement(MenuItem, { value: "Nieuw" }, "Nieuw"),
                React.createElement(MenuItem, { value: "Afgerond" }, "Afgerond"),
                React.createElement(MenuItem, { value: "Nog te doen" }, "Nog te doen"),
                React.createElement(MenuItem, { value: "Bulk closed" }, "Bulk closed")));
        },
    },
];
// Function to create columns with search highlighting
const percentColors = (value) => {
    if (value >= 90)
        return { path: "#63B450", trail: "#E7F4E4" }; // green
    if (value >= 70)
        return { path: "#4DB6F6", trail: "#E5F4FD" }; // blue
    if (value >= 40)
        return { path: "#E7B02C", trail: "#FFF4DC" }; // yellow
    return { path: "#D64F3B", trail: "#FBE4DF" }; // red
};
const getSnapshotRowId = (row, fallbackIndex) => {
    const keyParts = [
        row.parentProjectId,
        row.parentProjectNumber,
        row.childProjectNumber,
        row.topParentProjectNumber,
        row.hierarchyLevel,
    ].filter((value) => value !== undefined && value !== null && value !== "");
    if (keyParts.length > 0) {
        return keyParts.join("::");
    }
    return fallbackIndex !== undefined ? `snapshot-${fallbackIndex}` : "snapshot";
};
const createColumns = (searchTerm, isSearchResult = false, unitType = "K") => [
    {
        field: "parentProjectNumber",
        headerName: "Projectnummer",
        flex: 1.5,
        minWidth: 150,
        sortable: false,
        filterOperators: stringFilterOperators,
    },
    {
        field: "entityName",
        headerName: "Projectleider",
        flex: 1.5,
        minWidth: 140,
        sortable: false,
        filterOperators: stringFilterOperators,
        renderCell: (params) => {
            const value = params.value || "";
            return isSearchResult && searchTerm && searchTerm.trim() !== ""
                ? highlightText(value, searchTerm)
                : value;
        },
    },
    {
        field: "parentProjectDescription",
        headerName: "Projectnaam",
        flex: 5,
        minWidth: 400,
        sortable: false,
        filterOperators: stringFilterOperators,
        renderCell: (params) => formatProjectName(params.value, searchTerm, isSearchResult, params.row.tooltipPath),
    },
    {
        field: "projectStatus",
        headerName: "Status",
        flex: 1.5,
        minWidth: 140,
        sortable: false,
        filterable: true,
        filterOperators: statusFilterOperators,
        renderCell: (params) => {
            var _a, _b, _c, _d;
            const rawValue = (_c = (_a = params.value) !== null && _a !== void 0 ? _a : (_b = params.row) === null || _b === void 0 ? void 0 : _b.projectStatus) !== null && _c !== void 0 ? _c : (_d = params.row) === null || _d === void 0 ? void 0 : _d.ProjectStatus;
            const value = rawValue !== null && rawValue !== void 0 ? rawValue : "";
            const displayValue = value && isSearchResult && searchTerm && searchTerm.trim() !== ""
                ? highlightText(String(value), searchTerm)
                : value || "—";
            // Determine color based on status value
            const getStatusColor = (status) => {
                const statusLower = status.toLowerCase().trim();
                if (statusLower === "afgerond") {
                    return "success"; // Green for completed
                }
                if (statusLower === "nog te doen") {
                    return "warning"; // Yellow for to be done
                }
                if (statusLower === "nieuw") {
                    return "info"; // Blue for new
                }
                return "primary"; // Default
            };
            return (React.createElement(Tooltip, { arrow: true, placement: "left", title: `Status: ${value || "—"}` },
                React.createElement(Chip, { label: displayValue, color: getStatusColor(value), variant: "soft", size: "small", sx: {
                        borderRadius: "24px",
                    } })));
        },
    },
    {
        field: "aanneemsom",
        headerName: "Aanneemsom",
        type: "number",
        flex: 1.5,
        minWidth: 140,
        sortable: false,
        filterable: true,
        headerAlign: "left",
        align: "left",
        filterOperators: numericFilterOperators,
        renderCell: (params) => {
            var _a;
            const rawValue = (_a = params.value) !== null && _a !== void 0 ? _a : "";
            const num = Number(rawValue);
            const absNum = Math.abs(num);
            // Format with K/M suffixes using Dutch convention or full number
            let display = rawValue;
            if (!isNaN(num)) {
                if (unitType === "full") {
                    display = absNum.toLocaleString("nl-NL", {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                    });
                }
                else if (unitType === "M") {
                    display = formatDutchNumberWithSuffix(absNum);
                }
                else {
                    // Default to K
                    display = formatDutchNumberWithKSuffixOnly(absNum);
                }
            }
            const displayValue = isSearchResult && (searchTerm === null || searchTerm === void 0 ? void 0 : searchTerm.trim())
                ? highlightText(String(display), searchTerm)
                : display;
            // Apply negative formatting with brackets
            const formattedValue = !isNaN(num) && num < 0 ? (React.createElement("span", { style: { color: "red" } },
                "(",
                displayValue,
                ")")) : (displayValue);
            // Format tooltip with same display format
            const tooltipValue = !isNaN(num) && num < 0 ? `(${display})` : display;
            return (React.createElement(Tooltip, { arrow: true, placement: "left", title: `Aanneemsom: ${tooltipValue}` },
                React.createElement("span", null, formattedValue)));
        },
    },
    {
        field: "forecastMargin",
        headerName: "Forecast Marge",
        type: "number",
        flex: 1,
        minWidth: 150,
        sortable: false,
        filterable: true,
        headerAlign: "left",
        align: "left",
        filterOperators: numericFilterOperators,
        renderCell: (params) => {
            // Convert value to number if possible
            let value = params.value;
            let num = typeof value === "string"
                ? parseFloat(value.replace(/[^\d.]/g, ""))
                : value;
            let display = value;
            const absNum = Math.abs(num);
            if (!isNaN(num)) {
                // Format with K/M suffixes using Dutch convention or full number
                if (unitType === "full") {
                    display = absNum.toLocaleString("nl-NL", {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                    });
                }
                else if (unitType === "M") {
                    display = formatDutchNumberWithSuffix(absNum);
                }
                else {
                    // Default to K
                    display = formatDutchNumberWithKSuffixOnly(absNum);
                }
            }
            // Format negative values
            const displayText = !isNaN(num) && num < 0 ? `(${String(display)})` : String(display);
            const chipLabel = isSearchResult && searchTerm && searchTerm.trim() !== ""
                ? highlightText(displayText, searchTerm)
                : displayText;
            // Format tooltip value based on unit type
            const tooltipValue = !isNaN(num)
                ? unitType === "full"
                    ? absNum.toLocaleString("nl-NL", {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                    })
                    : unitType === "M"
                        ? formatDutchNumberWithSuffix(absNum)
                        : formatDutchNumberWithKSuffixOnly(absNum)
                : value;
            const tooltipDisplay = !isNaN(num) && num < 0 ? `(${tooltipValue})` : tooltipValue;
            return (React.createElement(Tooltip, { arrow: true, placement: "left", title: `Forecast Marge: ${tooltipDisplay}` },
                React.createElement(Chip, { label: chipLabel, color: "primary", variant: "soft", size: "small", sx: Object.assign({ borderRadius: "24px" }, (isNaN(num) || num >= 0
                        ? {}
                        : {
                            color: "error.main",
                            "& .MuiChip-label": {
                                color: "error.main",
                            },
                        })) })));
        },
    },
    {
        field: "forecastMarginPercent",
        headerName: "%Forecast Marge",
        flex: 1,
        minWidth: 160,
        sortable: false,
        filterable: true,
        filterOperators: numericFilterOperators,
        renderCell: (params) => {
            const percent = Number(params.value) || 0;
            const { path, trail } = percentColors(percent);
            const formattedPercent = percent.toLocaleString("nl-NL", {
                minimumFractionDigits: 0,
                maximumFractionDigits: 2,
            });
            return (React.createElement(Tooltip, { arrow: true, placement: "left", title: `Forecast Marge Percent: ${formattedPercent}%` },
                React.createElement(Box, { width: 42, height: 42 },
                    React.createElement(CircularProgressbar, { value: percent, text: formattedPercent, strokeWidth: 10, styles: buildStyles({
                            pathColor: path,
                            trailColor: "transparent",
                            textColor: "#161C24",
                            textSize: "24px",
                            strokeLinecap: "round",
                        }) }))));
        },
    },
    {
        field: "afgerondPercent",
        headerName: "POC %",
        flex: 1,
        minWidth: 160,
        sortable: false,
        filterOperators: numericFilterOperators,
        renderCell: (params) => {
            const percent = Number(params.value) || 0;
            const { path, trail } = percentColors(percent);
            // Display exact value from backend without rounding
            // Show "0" for zero values, otherwise show 1 decimal place
            const formattedPercent = percent === 0
                ? "0"
                : percent.toLocaleString("nl-NL", {
                    minimumFractionDigits: 1,
                    maximumFractionDigits: 1,
                });
            return (React.createElement(Tooltip, { arrow: true, placement: "left", title: `POC Percentage: ${formattedPercent}%` },
                React.createElement(Box, { width: 42, height: 42 },
                    React.createElement(CircularProgressbar, { value: percent, text: formattedPercent, strokeWidth: 10, styles: buildStyles({
                            pathColor: path,
                            trailColor: "transparent",
                            textColor: "#161C24",
                            textSize: "24px",
                            strokeLinecap: "round",
                        }) }))));
        },
    },
    {
        field: "gefactureerdPTD",
        headerName: "Gefactureerd PTD",
        type: "number",
        flex: 0.9,
        minWidth: 160,
        sortable: false,
        filterable: true,
        headerAlign: "left",
        align: "left",
        filterOperators: numericFilterOperators,
        renderCell: (params) => {
            const rawValue = params.value || "";
            const num = Number(rawValue);
            const absNum = Math.abs(num);
            // Format with K/M suffixes using Dutch convention or full number
            let display = rawValue;
            if (!isNaN(num)) {
                if (unitType === "full") {
                    display = absNum.toLocaleString("nl-NL", {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                    });
                }
                else if (unitType === "M") {
                    display = formatDutchNumberWithSuffix(absNum);
                }
                else {
                    // Default to K
                    display = formatDutchNumberWithKSuffixOnly(absNum);
                }
            }
            const displayValue = isSearchResult && (searchTerm === null || searchTerm === void 0 ? void 0 : searchTerm.trim())
                ? highlightText(String(display), searchTerm)
                : display;
            // Apply negative formatting with brackets
            const formattedValue = !isNaN(num) && num < 0 ? (React.createElement("span", { style: { color: "red" } },
                "(",
                displayValue,
                ")")) : (displayValue);
            // Format tooltip with same display format
            const tooltipValue = !isNaN(num) && num < 0 ? `(${display})` : display;
            return (React.createElement(Tooltip, { arrow: true, placement: "left", title: `Gefactureerd PTD: ${tooltipValue}` },
                React.createElement("span", null, formattedValue)));
        },
    },
    {
        field: "kostenPTD",
        headerName: "Kosten PTD",
        type: "number",
        flex: 0.9,
        minWidth: 120,
        sortable: false,
        filterable: true,
        headerAlign: "left",
        align: "left",
        filterOperators: numericFilterOperators,
        renderCell: (params) => {
            const rawValue = params.value || "";
            const num = Number(rawValue);
            const absNum = Math.abs(num);
            // Format with K/M suffixes using Dutch convention or full number
            let display = rawValue;
            if (!isNaN(num)) {
                if (unitType === "full") {
                    display = absNum.toLocaleString("nl-NL", {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                    });
                }
                else if (unitType === "M") {
                    display = formatDutchNumberWithSuffix(absNum);
                }
                else {
                    // Default to K
                    display = formatDutchNumberWithKSuffixOnly(absNum);
                }
            }
            const displayValue = isSearchResult && (searchTerm === null || searchTerm === void 0 ? void 0 : searchTerm.trim())
                ? highlightText(String(display), searchTerm)
                : display;
            // Apply negative formatting with brackets
            const formattedValue = !isNaN(num) && num < 0 ? (React.createElement("span", { style: { color: "red" } },
                "(",
                displayValue,
                ")")) : (displayValue);
            // Format tooltip with same display format
            const tooltipValue = !isNaN(num) && num < 0 ? `(${display})` : display;
            return (React.createElement(Tooltip, { arrow: true, placement: "left", title: `Kosten PTD: ${tooltipValue}` },
                React.createElement("span", null, formattedValue)));
        },
    },
    {
        field: "ohw",
        headerName: "OHW",
        type: "number",
        flex: 0.6,
        minWidth: 100,
        sortable: false,
        filterable: true,
        headerAlign: "left",
        align: "left",
        filterOperators: numericFilterOperators,
        renderCell: (params) => {
            const rawValue = params.value || "";
            const num = Number(rawValue);
            const absNum = Math.abs(num);
            // Format with K/M suffixes using Dutch convention or full number
            let display = rawValue;
            if (!isNaN(num)) {
                if (unitType === "full") {
                    display = absNum.toLocaleString("nl-NL", {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                    });
                }
                else if (unitType === "M") {
                    display = formatDutchNumberWithSuffix(absNum);
                }
                else {
                    // Default to K
                    display = formatDutchNumberWithKSuffixOnly(absNum);
                }
            }
            const displayValue = isSearchResult && (searchTerm === null || searchTerm === void 0 ? void 0 : searchTerm.trim())
                ? highlightText(String(display), searchTerm)
                : display;
            // Apply negative formatting with brackets
            const formattedValue = !isNaN(num) && num < 0 ? (React.createElement("span", { style: { color: "red" } },
                "(",
                displayValue,
                ")")) : (displayValue);
            // Format tooltip with same display format
            const tooltipValue = !isNaN(num) && num < 0 ? `(${display})` : display;
            return (React.createElement(Tooltip, { arrow: true, placement: "left", title: `OHW: ${tooltipValue}` },
                React.createElement("span", null, formattedValue)));
        },
    },
    {
        field: "directChildrenCount",
        headerName: "# Deelprojecten",
        flex: 1.5,
        minWidth: 140,
        sortable: false,
        filterOperators: stringFilterOperators,
    },
];
export const Home = () => {
    var _a;
    const theme = useTheme();
    const isMobile = useMediaQuery((theme) => theme.breakpoints.down("md"));
    const [page, setPage] = React.useState(0);
    const [pageSize, setPageSize] = React.useState(10);
    const [snapshotsData, setSnapshotsData] = React.useState([]);
    const [totalRowCount, setTotalRowCount] = React.useState(0);
    const [searchTerm, setSearchTerm] = React.useState("");
    const [debouncedSearchTerm, setDebouncedSearchTerm] = React.useState("");
    const [aanneemsom, setAanneemsom] = React.useState("");
    const [debouncedAanneemsom, setDebouncedAanneemsom] = React.useState("");
    const [unitType, setUnitType] = React.useState("full");
    const [filterModel, setFilterModel] = React.useState({
        items: [],
    });
    const [debouncedFilterModel, setDebouncedFilterModel] = React.useState({
        items: [],
    });
    const previousFilterValuesRef = useRef("");
    const [sortModel, setSortModel] = React.useState([]);
    const isSortingRef = useRef(false);
    // Start with loading state true to show skeleton immediately on mount
    const [isSearching, setIsSearching] = React.useState(true);
    const navigate = useNavigate();
    const [getProjectSnapshots, { isLoading: snapshotsLoading }] = useGetProjectSnapshotsMutation();
    const [totalProjectsCount, setTotalProjectsCount] = React.useState(null);
    const [newProjectsCount, setNewProjectsCount] = React.useState(null);
    const [inProgressProjectsCount, setInProgressProjectsCount] = React.useState(null);
    const [bulkClosedProjectsCount, setBulkClosedProjectsCount] = React.useState(null);
    const [afgerondProjectsCount, setAfgerondProjectsCount] = React.useState(null);
    const [totalsRow, setTotalsRow] = React.useState(null);
    // Track last request to prevent duplicate calls
    const lastRequestRef = useRef("");
    const isFetchingRef = useRef(false);
    // Tab state for status filtering
    const [statusTab, setStatusTab] = React.useState("all");
    // Sync tab state with filter model (only when filter changes externally, not from tab clicks)
    const isTabChangeRef = useRef(false);
    // Handle status tab changes
    const handleStatusTabChange = (event, newValue) => {
        isTabChangeRef.current = true;
        setStatusTab(newValue);
        setSearchTerm("");
        if (newValue === "all") {
            setFilterModel({ items: [] });
            setDebouncedFilterModel({ items: [] });
            previousFilterValuesRef.current = "";
            setPage(0);
        }
        else {
            const statusMap = {
                new: "Nieuw",
                afgerond: "Afgerond",
                inprogress: "Nog te doen",
                bulkclosed: "Bulk closed",
            };
            const newFilterModel = {
                items: [
                    {
                        id: `status-tab-${newValue}`,
                        field: "projectStatus",
                        operator: "is",
                        value: statusMap[newValue],
                    },
                ],
            };
            setFilterModel(newFilterModel);
            setDebouncedFilterModel(newFilterModel);
            previousFilterValuesRef.current = `projectStatus:${statusMap[newValue]}`;
            setPage(0);
        }
    };
    React.useEffect(() => {
        var _a;
        if (isTabChangeRef.current) {
            isTabChangeRef.current = false;
            return;
        }
        const statusFilter = (_a = filterModel.items) === null || _a === void 0 ? void 0 : _a.find((item) => item.field === "projectStatus" && item.operator === "is");
        if (!statusFilter) {
            if (statusTab !== "all") {
                setStatusTab("all");
            }
        }
        else {
            const statusValue = statusFilter.value;
            const tabMap = {
                Nieuw: "new",
                Afgerond: "afgerond",
                "Nog te doen": "inprogress",
                "Bulk closed": "bulkclosed",
            };
            const expectedTab = tabMap[statusValue] || "all";
            if (statusTab !== expectedTab) {
                setStatusTab(expectedTab);
            }
        }
    }, [filterModel]);
    // Cleanup refs and reset loading state when component unmounts to prevent state persistence
    useEffect(() => {
        return () => {
            lastRequestRef.current = "";
            isFetchingRef.current = false;
            setIsSearching(false);
            try {
                // Hide any DataGrid loading overlays that might persist - don't remove to avoid React removeChild errors
                const dataGridOverlays = document.querySelectorAll(".MuiDataGrid-overlayWrapper, .MuiDataGrid-overlay, .MuiDataGrid-loadingOverlay");
                dataGridOverlays.forEach((overlay) => {
                    try {
                        const overlayElement = overlay;
                        // Just hide it - don't remove to avoid React removeChild errors
                        overlayElement.style.setProperty("display", "none", "important");
                        overlayElement.style.setProperty("pointer-events", "none", "important");
                        overlayElement.style.setProperty("z-index", "-1", "important");
                    }
                    catch (error) {
                        // Silently ignore errors for individual elements
                    }
                });
                // Hide any backdrops - don't remove to avoid React removeChild errors
                const backdrops = document.querySelectorAll(".MuiBackdrop-root");
                backdrops.forEach((backdrop) => {
                    try {
                        const backdropElement = backdrop;
                        // Only hide if it's not part of an active dialog/modal
                        const parent = backdropElement.parentElement;
                        const isActiveDialog = (parent === null || parent === void 0 ? void 0 : parent.classList.contains("MuiModal-root")) ||
                            (parent === null || parent === void 0 ? void 0 : parent.classList.contains("MuiDialog-root"));
                        if (!isActiveDialog) {
                            // Just hide it - don't remove to avoid React removeChild errors
                            backdropElement.style.setProperty("display", "none", "important");
                            backdropElement.style.setProperty("pointer-events", "none", "important");
                            backdropElement.style.setProperty("z-index", "-1", "important");
                        }
                    }
                    catch (error) {
                        // Silently ignore errors for individual elements
                    }
                });
            }
            catch (error) {
                // Silently ignore cleanup errors
            }
        };
    }, []);
    const { enqueueSnackbar } = useSnackbar();
    const { canRead, isLoading: isLoadingPermissions } = usePermissions();
    // Get selected companies from context
    const { getSelectedCompanyNames, getSelectedCompanyIds, selectedCompanyId, companies, isLoadingCompanies, hasPendingChanges, companyMonthConfigs, } = useCompany();
    // Use a ref to track previous config to avoid unnecessary refetches
    const prevConfigRef = React.useRef("");
    // Create a stable reference to config month to detect actual changes
    const configMonthKey = React.useMemo(() => {
        if (!companyMonthConfigs || companyMonthConfigs.length === 0) {
            return "";
        }
        return JSON.stringify(companyMonthConfigs
            .map((c) => ({ companyId: c.companyId, configMonth: c.configMonth }))
            .sort((a, b) => a.companyId.localeCompare(b.companyId)));
    }, [companyMonthConfigs]);
    // Track config changes separately to avoid multiple calls
    React.useEffect(() => {
        if (prevConfigRef.current !== configMonthKey &&
            prevConfigRef.current !== "") {
            // Config actually changed, trigger refetch by resetting page
            setPage(0);
        }
        prevConfigRef.current = configMonthKey;
    }, [configMonthKey]);
    // Reset pagination when component mounts (e.g., when navigating back from logo click)
    useEffect(() => {
        setPage(0);
    }, []);
    // Handle sort model changes and prevent page reset
    const handleSortModelChange = React.useCallback((newModel) => {
        isSortingRef.current = true;
        setSortModel(newModel);
        // Reset flag after a short delay to allow pagination callback to fire
        setTimeout(() => {
            isSortingRef.current = false;
        }, 100);
    }, []);
    // Clear all filters and search
    const handleClearAll = () => {
        setSearchTerm("");
        setDebouncedSearchTerm("");
        setFilterModel({ items: [] });
        setDebouncedFilterModel({ items: [] });
        previousFilterValuesRef.current = "";
        setPage(0);
    };
    // Debounce search term
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 2000); // 2000ms delay
        return () => clearTimeout(timer);
    }, [searchTerm]);
    // Debounce aanneemsom filter
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedAanneemsom(aanneemsom);
        }, 2000); // 2000ms delay
        return () => clearTimeout(timer);
    }, [aanneemsom]);
    // Debounce filter model changes - only trigger when there's actual filter values being typed
    useEffect(() => {
        var _a;
        // Extract current filter values as a string for comparison
        const currentFilterValues = ((_a = filterModel.items) === null || _a === void 0 ? void 0 : _a.filter((item) => item.value !== undefined &&
            item.value !== null &&
            item.value !== "").map((item) => `${item.field}:${String(item.value)}`).sort().join("|")) || "";
        // Only trigger debounce if values actually changed (user typed something)
        // If current values are empty and previous were empty, don't trigger (just opening/closing filter)
        if (currentFilterValues === previousFilterValuesRef.current) {
            return;
        }
        // If clearing filters (empty string), update immediately without debounce
        if (currentFilterValues === "" && previousFilterValuesRef.current !== "") {
            previousFilterValuesRef.current = "";
            setDebouncedFilterModel(filterModel);
            setPage(0);
            return;
        }
        // If no filter values, skip debounce
        if (currentFilterValues === "") {
            return;
        }
        // Update the ref immediately to track the change
        previousFilterValuesRef.current = currentFilterValues;
        const timer = setTimeout(() => {
            // Allow update if:
            // 1. There are filter values (user is filtering)
            // 2. OR current values are empty BUT we previously had filters (user is clearing)
            if (currentFilterValues !== "") {
                setDebouncedFilterModel(filterModel);
                setPage(0);
            }
        }, 2000); // 2000ms delay
        return () => clearTimeout(timer);
    }, [filterModel]);
    // Call snapshots API with search term, filters, pagination, and sorting
    useEffect(() => {
        const fetchSnapshots = () => __awaiter(void 0, void 0, void 0, function* () {
            // Skip API call if there are pending company changes (waiting for redirect)
            if (hasPendingChanges) {
                return;
            }
            // Get selected company names - try from context first, then localStorage as fallback
            let selectedCompanyNames = getSelectedCompanyNames();
            // If no selection from context, try localStorage immediately (don't wait for companies to load)
            if (!selectedCompanyNames || selectedCompanyNames.length === 0) {
                const stored = localStorage.getItem("selectedCompanies");
                if (stored) {
                    try {
                        selectedCompanyNames = JSON.parse(stored);
                    }
                    catch (e) {
                        // Invalid JSON, ignore
                    }
                }
            }
            // Skip API call if no companies are selected
            if (!selectedCompanyNames || selectedCompanyNames.length === 0) {
                // Keep loading state if companies are still loading (will auto-select)
                // Only clear data if companies are loaded and we're sure no selection will happen
                if (!isLoadingCompanies && companies && companies.length > 0) {
                    setSnapshotsData([]);
                    setTotalRowCount(0);
                    // Don't set isSearching to false here - let the waitingForCompanyId logic handle it
                    // This ensures skeleton stays visible during auto-selection
                }
                else if (isLoadingCompanies) {
                    // Keep loading state while companies are loading (handles incognito case)
                    setIsSearching(true);
                }
                return;
            }
            // Build request object first to create a stable key
            const filters = [];
            // Define numeric fields (must match backend switch cases)
            const numericFields = [
                "aanneemsom",
                "omzet",
                "ohw",
                "afgerondpercent",
                "forecastmargin",
                "forecastmarginpercent",
            ];
            // Map MUI DataGrid operators to backend operators
            const mapOperator = (muiOperator, field) => {
                if (!muiOperator) {
                    // Default operator based on field type
                    return numericFields.includes(field) ? "gte" : "contains";
                }
                // Map MUI operator names to backend operator names
                // MUI DataGrid uses camelCase operator names
                const operatorMap = {
                    // Symbol operators (if used)
                    "=": "eq",
                    "!=": "neq",
                    ">": "gt",
                    ">=": "gte",
                    "<": "lt",
                    "<=": "lte",
                    // CamelCase operators (standard MUI DataGrid format)
                    equals: "eq",
                    notEquals: "neq",
                    greaterThan: "gt",
                    greaterThanOrEqual: "gte",
                    lessThan: "lt",
                    lessThanOrEqual: "lte",
                    contains: "contains",
                    startsWith: "startsWith",
                    endsWith: "endsWith",
                    // Already in correct format
                    eq: "eq",
                    neq: "neq",
                    gt: "gt",
                    gte: "gte",
                    lt: "lt",
                    lte: "lte",
                };
                // Normalize operator (lowercase for case-insensitive matching)
                const normalizedOperator = muiOperator.toLowerCase();
                return (operatorMap[normalizedOperator] ||
                    operatorMap[muiOperator] ||
                    muiOperator);
            };
            if (debouncedFilterModel.items) {
                debouncedFilterModel.items.forEach((filterItem) => {
                    if (filterItem.value !== undefined &&
                        filterItem.value !== null &&
                        filterItem.value !== "") {
                        // Use the actual operator from filterItem, mapped to backend format
                        const operator = mapOperator(filterItem.operator, filterItem.field);
                        filters.push({
                            field: filterItem.field,
                            operator_: operator,
                            value: String(filterItem.value),
                        });
                    }
                });
            }
            // Note: projectDescriptionSearch is now passed separately, not as a filter
            // Add aanneemsom filter if provided
            if (debouncedAanneemsom && debouncedAanneemsom.trim() !== "") {
                filters.push({
                    field: "aanneemsom",
                    operator_: "contains",
                    value: debouncedAanneemsom,
                });
            }
            // Build pagination object (convert from 0-based to 1-based)
            const pagination = {
                page: page + 1, // Convert from 0-based to 1-based
                pageSize,
            };
            // Get company ID - find by selected company name
            // Use selectedCompanyNames from above (may come from localStorage or context)
            let companyId = "";
            // Try to find companyId from companies array by matching entiteitNaam
            if (companies &&
                companies.length > 0 &&
                selectedCompanyNames.length > 0) {
                const selectedName = selectedCompanyNames[0];
                const company = companies.find((c) => c.entiteitNaam === selectedName);
                companyId = (company === null || company === void 0 ? void 0 : company.entiteitId) || "";
                // If still not found, try getSelectedCompanyIds as fallback
                if (!companyId) {
                    const companyIds = getSelectedCompanyIds();
                    companyId = companyIds.length > 0 ? companyIds[0] : "";
                }
            }
            // Also try selectedCompanyId from context as last resort
            if (!companyId && selectedCompanyId) {
                companyId = selectedCompanyId;
            }
            // CompanyId is required for calculations - wait for it to be available
            // This will show skeleton loader while waiting
            if (!companyId) {
                // Set loading state to show skeleton
                if (snapshotsData.length === 0) {
                    setIsSearching(true);
                }
                return;
            }
            // Build request object matching backend structure
            const request = Object.assign(Object.assign({ entiteitNaam: selectedCompanyNames, pagination,
                companyId }, (filters.length > 0 ? { filters } : {})), (debouncedSearchTerm && debouncedSearchTerm.trim() !== ""
                ? { projectDescriptionSearch: debouncedSearchTerm.trim() }
                : {}));
            // Debug logs removed.
            // Create a stable key from the request to prevent duplicate calls
            const requestKey = JSON.stringify(request);
            // Skip if this is the same request as the last one or if already fetching
            if (lastRequestRef.current === requestKey || isFetchingRef.current) {
                return;
            }
            // Mark as fetching and store the request key
            isFetchingRef.current = true;
            lastRequestRef.current = requestKey;
            try {
                setIsSearching(true);
                const result = yield getProjectSnapshots(request).unwrap();
                // Normalize casing (backend might return ProjectStatus vs projectStatus)
                const normalizedRows = (result.data || []).map((row) => {
                    var _a, _b, _c, _d, _e;
                    return (Object.assign(Object.assign({}, row), { projectStatus: (_a = row === null || row === void 0 ? void 0 : row.projectStatus) !== null && _a !== void 0 ? _a : row === null || row === void 0 ? void 0 : row.ProjectStatus, gefactureerdPTD: (_c = (_b = row === null || row === void 0 ? void 0 : row.gefactureerd) !== null && _b !== void 0 ? _b : row === null || row === void 0 ? void 0 : row.Gefactureerd) !== null && _c !== void 0 ? _c : 0, kostenPTD: (_e = (_d = row === null || row === void 0 ? void 0 : row.kosten) !== null && _d !== void 0 ? _d : row === null || row === void 0 ? void 0 : row.Kosten) !== null && _e !== void 0 ? _e : 0 }));
                });
                setSnapshotsData(normalizedRows);
                // Backend returns 1-based page, but we need to convert back to 0-based for the grid
                setTotalRowCount(result.totalCount);
                // Set KPIs and totals from backend response statusCounts object
                if (result.statusCounts) {
                    setTotalProjectsCount(result.statusCounts.all);
                    setNewProjectsCount(result.statusCounts.nieuwCount);
                    setInProgressProjectsCount(result.statusCounts.nogTeDoenCount);
                    setBulkClosedProjectsCount(result.statusCounts.bulkClosedCount);
                    setAfgerondProjectsCount(result.statusCounts.afgerondCount);
                }
                // Set totals row from backend response
                if (result.totals) {
                    setTotalsRow({
                        isTotals: true,
                        parentProjectNumber: "Totaal",
                        aanneemsom: result.totals.aanneemsom || 0,
                        forecastMargin: result.totals.forecastMargin || 0,
                        gefactureerdPTD: result.totals.gefactureerd || result.totals.Gefactureerd || 0,
                        kostenPTD: result.totals.kosten || result.totals.Kosten || 0,
                        ohw: result.totals.ohw || 0,
                    });
                }
                else {
                    setTotalsRow(null);
                }
            }
            catch (error) {
                setSnapshotsData([]);
                setTotalRowCount(0);
                enqueueSnackbar("Fout bij het ophalen van projectgegevens", {
                    variant: "error",
                });
                // Reset request key on error so it can retry
                lastRequestRef.current = "";
            }
            finally {
                setIsSearching(false);
                isFetchingRef.current = false;
            }
        });
        fetchSnapshots();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        hasPendingChanges,
        debouncedSearchTerm,
        debouncedAanneemsom,
        debouncedFilterModel,
        page,
        pageSize,
        isLoadingCompanies,
        companies,
        selectedCompanyId,
    ]);
    // Determine if we're showing search results (only when there's a search term and we're not loading)
    const hasSearchTerm = debouncedSearchTerm
        ? debouncedSearchTerm.trim() !== ""
        : false;
    const isSearchResult = hasSearchTerm;
    // Check if we're waiting for companyId (required for calculations)
    // Get selected company names - try from context first, then localStorage as fallback
    let selectedCompanyNames = getSelectedCompanyNames();
    if (!selectedCompanyNames || selectedCompanyNames.length === 0) {
        const stored = localStorage.getItem("selectedCompanies");
        if (stored) {
            try {
                selectedCompanyNames = JSON.parse(stored);
            }
            catch (e) {
                // Invalid JSON, ignore
            }
        }
    }
    // Determine if we're waiting - show skeleton in these cases:
    // 1. Companies are still loading (initial load or incognito)
    // 2. Company is selected but companyId is not available yet
    // 3. No company selected yet but companies are loading (will auto-select)
    let waitingForCompanyId = false;
    let hasSelectedCompany = selectedCompanyNames && selectedCompanyNames.length > 0;
    if (isLoadingCompanies) {
        // Always show skeleton while companies are loading (handles incognito case)
        waitingForCompanyId = true;
    }
    else if (hasSelectedCompany) {
        // Check if we have companyId available for selected company
        let hasCompanyId = false;
        if (companies && companies.length > 0) {
            const selectedName = selectedCompanyNames[0];
            const company = companies.find((c) => c.entiteitNaam === selectedName);
            hasCompanyId = !!(company === null || company === void 0 ? void 0 : company.entiteitId);
        }
        if (!hasCompanyId && selectedCompanyId) {
            hasCompanyId = true;
        }
        // We're waiting if we don't have companyId yet
        waitingForCompanyId = !hasCompanyId;
    }
    // Show loading state if waiting and no data yet
    React.useEffect(() => {
        if (waitingForCompanyId && snapshotsData.length === 0) {
            setIsSearching(true);
        }
        else if (!waitingForCompanyId && snapshotsData.length > 0) {
            // Only set to false if we have data and not waiting
            // Keep true if still fetching
            if (!snapshotsLoading) {
                setIsSearching(false);
            }
        }
    }, [waitingForCompanyId, snapshotsData.length, snapshotsLoading]);
    const showContentLoader = isSearching || snapshotsLoading || waitingForCompanyId;
    const showInitialLoader = showContentLoader && snapshotsData.length === 0;
    const columns = useMemo(() => createColumns(debouncedSearchTerm, isSearchResult, unitType), [debouncedSearchTerm, isSearchResult, unitType]);
    // Format config month to "December 2025" format
    const formatConfigMonth = (configMonth) => {
        try {
            const date = new Date(configMonth);
            const monthNames = [
                "januari",
                "februari",
                "maart",
                "april",
                "mei",
                "juni",
                "juli",
                "augustus",
                "september",
                "oktober",
                "november",
                "december",
            ];
            const month = monthNames[date.getMonth()];
            const year = date.getFullYear();
            return `${month} ${year}`;
        }
        catch (_a) {
            return configMonth;
        }
    };
    const forecastDataMonth = companyMonthConfigs && companyMonthConfigs.length > 0
        ? formatConfigMonth(companyMonthConfigs[0].configMonth)
        : "";
    return (React.createElement(Box, null,
        React.createElement(PageHeader, { title: "Projecten", subtitle: forecastDataMonth
                ? `Forecast Data Month: ${forecastDataMonth}`
                : undefined, actions: React.createElement(React.Fragment, null, !isMobile && (React.createElement(Box, { display: "flex", alignItems: "center", gap: 2 },
                React.createElement(FormControl, { size: "medium", sx: { minWidth: 120 } },
                    React.createElement(InputLabel, { id: "unit-select-label" }, "Eenheid"),
                    React.createElement(Select, { size: "medium", labelId: "unit-select-label", id: "unit-select", value: unitType, label: "Eenheid", onChange: (e) => setUnitType(e.target.value), sx: {
                            fontSize: "0.875rem",
                            borderRadius: "40px",
                            "& .MuiSelect-select": {
                                padding: "12px 16px",
                            },
                        } },
                        React.createElement(MenuItem, { value: "K" }, "K (Duizend)"),
                        React.createElement(MenuItem, { value: "full" }, "Volledig"))),
                React.createElement(FilteredInput, { placeholder: "Zoeken", value: searchTerm, onChange: (e) => setSearchTerm(e.target.value), endIcon: React.createElement(SearchNormal, { size: 20, color: "currentColor" }) })))) }),
        isLoadingPermissions ? (React.createElement(Box, { sx: { p: 4, textAlign: "center" } },
            React.createElement(CircularProgress, null))) : !canRead("Projects") ? (React.createElement(Box, { sx: { p: 4, textAlign: "center" } },
            React.createElement(Typography, { variant: "h6", color: "error" }, "U heeft geen toegang tot de projecten module"))) : (React.createElement(Box, { sx: {
                height: isMobile ? "calc(100dvh - 158px)" : "calc(100dvh -220px)",
                overflow: "auto",
                pb: 1,
            } },
            totalsRow && (React.createElement(Box, { sx: {
                    display: "grid",
                    gridTemplateColumns: {
                        xs: "1fr",
                        sm: "repeat(2, 1fr)",
                        md: "repeat(5, 1fr)",
                    },
                    gap: 2,
                } },
                React.createElement(MetricCard, { title: "Totaal Aanneemsom", value: formatTotalValue(totalsRow.aanneemsom || 0, unitType), variant: isMobile ? "h3" : "h4", titlePlacement: "belowValue", left: {
                        variant: "icon",
                        icon: (React.createElement(Moneys, { variant: "Bulk", size: "50px", color: theme.palette.success.main })),
                    } }),
                React.createElement(MetricCard, { title: "Totaal Forecast Marge", value: formatTotalValue(totalsRow.forecastMargin || 0, unitType), variant: isMobile ? "h3" : "h4", titlePlacement: "belowValue", left: {
                        variant: "icon",
                        icon: (React.createElement(StatusUp, { variant: "Bulk", size: "50px", color: theme.palette.warning.dark })),
                    } }),
                React.createElement(MetricCard, { title: "Totaal Gefactureerd PTD", value: formatTotalValue(totalsRow.gefactureerdPTD || 0, unitType), variant: isMobile ? "h3" : "h4", titlePlacement: "belowValue", left: {
                        variant: "icon",
                        icon: (React.createElement(MoneyRecive, { variant: "Bulk", size: "50px", color: theme.palette.info.main })),
                    } }),
                React.createElement(MetricCard, { title: "Totaal Kosten PTD", value: formatTotalValue(totalsRow.kostenPTD || 0, unitType), variant: isMobile ? "h3" : "h4", titlePlacement: "belowValue", left: {
                        variant: "icon",
                        icon: (React.createElement(MoneyRecive, { variant: "Bulk", size: "50px", color: theme.palette.error.main })),
                    } }),
                React.createElement(MetricCard, { title: "Totaal OHW", value: formatTotalValue(totalsRow.ohw || 0, unitType), variant: isMobile ? "h3" : "h4", titlePlacement: "belowValue", left: {
                        variant: "icon",
                        icon: (React.createElement(Moneys, { variant: "Bulk", size: "50px", color: theme.palette.primary.main })),
                    } }))),
            React.createElement(Box, { my: 1.5 },
                React.createElement(Tabs, { value: statusTab, onChange: handleStatusTabChange, "aria-label": "project status tabs", variant: "scrollable", sx: { backgroundColor: "background.paper", borderRadius: "10px" } },
                    React.createElement(Tab, { label: React.createElement(Box, { sx: { display: "flex", alignItems: "center", gap: 1 } },
                            React.createElement("span", null, "All"),
                            React.createElement(Chip, { label: (_a = totalProjectsCount !== null && totalProjectsCount !== void 0 ? totalProjectsCount : totalRowCount) !== null && _a !== void 0 ? _a : 0, size: "small", sx: {
                                    height: 20,
                                    minWidth: 20,
                                    backgroundColor: theme.palette.grey[300],
                                    color: theme.palette.text.primary,
                                    borderRadius: "500px",
                                } })), value: "all" }),
                    React.createElement(Tab, { label: React.createElement(Box, { sx: { display: "flex", alignItems: "center", gap: 1 } },
                            React.createElement("span", null, "Nieuw"),
                            React.createElement(Chip, { label: newProjectsCount !== null && newProjectsCount !== void 0 ? newProjectsCount : 0, size: "small", sx: {
                                    height: 20,
                                    minWidth: 20,
                                    backgroundColor: theme.palette.info.light,
                                    color: theme.palette.text.primary,
                                    borderRadius: "500px",
                                } })), value: "new" }),
                    React.createElement(Tab, { label: React.createElement(Box, { sx: { display: "flex", alignItems: "center", gap: 1 } },
                            React.createElement("span", null, "Afgerond"),
                            React.createElement(Chip, { label: afgerondProjectsCount !== null && afgerondProjectsCount !== void 0 ? afgerondProjectsCount : 0, size: "small", sx: {
                                    height: 20,
                                    minWidth: 20,
                                    backgroundColor: theme.palette.success.light,
                                    color: theme.palette.text.primary,
                                    borderRadius: "500px",
                                } })), value: "afgerond" }),
                    React.createElement(Tab, { label: React.createElement(Box, { sx: { display: "flex", alignItems: "center", gap: 1 } },
                            React.createElement("span", null, "Nog te doen"),
                            React.createElement(Chip, { label: inProgressProjectsCount !== null && inProgressProjectsCount !== void 0 ? inProgressProjectsCount : 0, size: "small", sx: {
                                    height: 20,
                                    minWidth: 20,
                                    backgroundColor: theme.palette.warning.light,
                                    color: theme.palette.text.primary,
                                    borderRadius: "500px",
                                } })), value: "inprogress" }),
                    React.createElement(Tab, { label: React.createElement(Box, { sx: { display: "flex", alignItems: "center", gap: 1 } },
                            React.createElement("span", null, "Massa afgerond"),
                            React.createElement(Chip, { label: bulkClosedProjectsCount !== null && bulkClosedProjectsCount !== void 0 ? bulkClosedProjectsCount : 0, size: "small", sx: {
                                    height: 20,
                                    minWidth: 20,
                                    backgroundColor: theme.palette.primary[20],
                                    color: theme.palette.text.primary,
                                    borderRadius: "500px",
                                } })), value: "bulkclosed" }))),
            isMobile ? (React.createElement(Box, { display: "flex", flexDirection: "column", gap: 2 },
                React.createElement(Box, { display: "flex", gap: 1, mb: 1, alignItems: "center", flexWrap: "wrap" },
                    React.createElement(FormControl, { size: "small", fullWidth: true },
                        React.createElement(InputLabel, { id: "unit-select-label-mobile" }, "Eenheid"),
                        React.createElement(Select, { labelId: "unit-select-label-mobile", id: "unit-select-mobile", value: unitType, label: "Eenheid", onChange: (e) => setUnitType(e.target.value) },
                            React.createElement(MenuItem, { value: "K" }, "K (Duizend)"),
                            React.createElement(MenuItem, { value: "M" }, "M (Miljoen)"),
                            React.createElement(MenuItem, { value: "full" }, "Volledig"))),
                    React.createElement(FilteredInput, { placeholder: "Zoeken", fullWidth: true, value: searchTerm, onChange: (e) => setSearchTerm(e.target.value) }),
                    React.createElement(FilteredInput, { fullWidth: true, placeholder: "Aanneemsom", type: "number", value: aanneemsom, onChange: (e) => setAanneemsom(e.target.value) })),
                showContentLoader ? (React.createElement(Box, { sx: { display: "grid", gap: 2, p: 2 } }, Array.from({ length: 6 }).map((_, index) => (React.createElement(Box, { key: index, sx: {
                        p: 3,
                        backgroundColor: "background.paper",
                        borderRadius: 2,
                        border: "1px solid",
                        borderColor: "divider",
                    } },
                    React.createElement(Skeleton, { variant: "text", width: "70%", height: 32, sx: { mb: 1 } }),
                    React.createElement(Skeleton, { variant: "text", width: "50%", height: 20, sx: { mb: 0.75 } }),
                    React.createElement(Skeleton, { variant: "text", width: "60%", height: 36, sx: { mb: 0.5 } }),
                    React.createElement(Skeleton, { variant: "rounded", width: "100%", height: 20, sx: { borderRadius: 1 } }),
                    React.createElement(Skeleton, { variant: "text", width: "40%", height: 20, sx: { mt: 0.75 } })))))) : (React.createElement(React.Fragment, null, snapshotsData === null || snapshotsData === void 0 ? void 0 :
                    snapshotsData.map((row, index) => {
                        var _a;
                        return (React.createElement(ProjectCard, { key: getSnapshotRowId(row, index), onClick: () => {
                                const projectId = row.parentProjectId || getSnapshotRowId(row, index);
                                const parentProjectNumber = row.parentProjectNumber;
                                const entityName = row.entityName;
                                const selectedCompanyId = getSelectedCompanyIds()[0] || "";
                                const params = new URLSearchParams({
                                    topProjectNumber: parentProjectNumber || "",
                                    entityName: entityName || "",
                                    companyId: selectedCompanyId,
                                });
                                if (debouncedAanneemsom &&
                                    debouncedAanneemsom.trim() !== "") {
                                    params.append("aanneemsom", debouncedAanneemsom);
                                }
                                navigate(`/project/${projectId}/details?${params.toString()}`);
                            }, projectName: formatProjectName(row.parentProjectDescription || "N/A", debouncedSearchTerm, isSearchResult, row.tooltipPath), projectStatus: (_a = row.projectStatus) !== null && _a !== void 0 ? _a : row.ProjectStatus, daysPassed: 60, totalDays: 100, omzet: row.omzet, percent: row.afgerondPercent, searchTerm: debouncedSearchTerm, isSearchResult: isSearchResult }));
                    }),
                    React.createElement(TablePagination, { component: "div", count: totalRowCount, page: page, onPageChange: (_, newPage) => setPage(newPage), rowsPerPage: pageSize, onRowsPerPageChange: (e) => {
                            setPageSize(parseInt(e.target.value, 10));
                            setPage(0);
                        }, rowsPerPageOptions: [5, 10, 15, 20], labelRowsPerPage: "Per pagina:", labelDisplayedRows: ({ from, to, count }) => `${from}-${to} van ${count !== -1 ? count : `meer dan ${to}`}`, sx: {
                            mt: 2,
                            borderTop: "1px solid",
                            borderColor: "divider",
                            backgroundColor: "background.paper",
                            borderRadius: 1,
                            "& .MuiTablePagination-toolbar": {
                                flexWrap: "wrap",
                                justifyContent: "center",
                                gap: 1,
                                py: 1,
                            },
                            "& .MuiTablePagination-selectLabel": {
                                m: 0,
                            },
                            "& .MuiTablePagination-displayedRows": {
                                m: 0,
                            },
                        } }))))) : (React.createElement(React.Fragment, null,
                (filterModel.items.length > 0 || searchTerm) && (React.createElement(Box, { sx: {
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        px: 2,
                        py: 1.5,
                        backgroundColor: "#F4F6F8",
                        borderBottom: "1px solid #E0E0E0",
                        mb: 0,
                    } },
                    React.createElement(Typography, { variant: "body2", sx: { color: "#637381" } },
                        filterModel.items.length + (searchTerm ? 1 : 0),
                        " filters applied"),
                    React.createElement(Button, { variant: "text", size: "small", onClick: handleClearAll, startIcon: React.createElement(CloseCircle, { size: 16, style: { marginRight: "-4px" } }), sx: {
                            textTransform: "none",
                            color: "#637381",
                            fontSize: "0.875rem",
                            minWidth: "auto",
                            px: 1,
                            "&:hover": {
                                backgroundColor: "rgba(0, 0, 0, 0.04)",
                            },
                        } }, "Wis filters"))),
                React.createElement(PrimaryGrid, { loading: showContentLoader, disableColumnFilter: false, disableColumnMenu: false, disableColumnSelector: true, filterMode: "server", paginationMode: "server", gridHeight: filterModel.items.length > 0
                        ? "calc(100dvh - 402px)"
                        : "calc(100dvh - 346px)", sortModel: sortModel, onSortModelChange: handleSortModelChange, filterModel: filterModel, onFilterModelChange: setFilterModel, rows: snapshotsData, columns: columns, pagination: true, heightVariant: "full", getRowId: (row) => getSnapshotRowId(row), paginationModel: { page, pageSize }, onPaginationModelChange: ({ page: newPage, pageSize: newPageSize, }) => {
                        // Prevent page reset to 0 when sorting (DataGrid tries to reset page on sort)
                        if (isSortingRef.current && newPage === 0 && page !== 0) {
                            return;
                        }
                        setPage(newPage);
                        setPageSize(newPageSize);
                    }, pageSizeOptions: [5, 10, 15, 20], rowCount: totalRowCount, slotProps: {
                        filterPanel: {
                            disableAddFilterButton: false,
                            disableRemoveAllButton: false,
                            columnsSort: "asc",
                            filterFormProps: {
                                columnInputProps: {
                                    disabled: true,
                                    readOnly: true,
                                },
                            },
                        },
                    }, sx: {
                        "& .MuiDataGrid-filterForm": {
                            "& .MuiDataGrid-filterFormDeleteIcon": {
                                order: 999,
                                marginLeft: "auto",
                            },
                        },
                        // Completely hide the column selector dropdown
                        "& .MuiDataGrid-filterFormColumnInput": {
                            display: "none !important",
                            visibility: "hidden !important",
                            width: 0,
                            margin: 0,
                            padding: 0,
                            "& *": {
                                display: "none !important",
                            },
                        },
                        // Style the value input as a search field
                        "& .MuiDataGrid-filterFormValueInput": {
                            flex: 1,
                            "& .MuiInputBase-root": {
                                borderRadius: "40px",
                                height: "45px",
                                padding: "12px 16px",
                            },
                        },
                    }, onRowClick: (params) => {
                        const projectId = params.row.parentProjectId || getSnapshotRowId(params.row);
                        const parentProjectNumber = params.row.parentProjectNumber;
                        const entityName = params.row.entityName;
                        const selectedCompanyId = getSelectedCompanyIds()[0] || "";
                        const urlParams = new URLSearchParams({
                            topProjectNumber: parentProjectNumber || "",
                            entityName: entityName || "",
                            companyId: selectedCompanyId,
                        });
                        if (debouncedAanneemsom &&
                            debouncedAanneemsom.trim() !== "") {
                            urlParams.append("aanneemsom", debouncedAanneemsom);
                        }
                        navigate(`/project/${projectId}/details?${urlParams.toString()}`);
                    } })))))));
};
